import { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { formatError } from "../utils/formatError";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { AuditEntry, AuditChange, User } from "../types";

const PAGE_SIZE = 50;

export function useAuditLog(enabled = true, agencyId: string | null = null) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [fetching, setFetching] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const cursorsRef = useRef<QueryDocumentSnapshot<unknown>[]>([]);

  const loading = enabled && !!agencyId && (loadedFor !== agencyId || fetching);

  const baseQuery = useCallback(() => {
    return query(
      collection(db, "auditLog"),
      where("agencyId", "==", agencyId),
      orderBy("timestamp", "desc"),
    );
  }, [agencyId]);

  const fetchPage = useCallback(async (pageNum: number) => {
    if (!enabled || !agencyId) return;
    setFetching(true);
    setError(null);
    try {
      let q;
      if (pageNum === 0) {
        q = query(baseQuery(), limit(PAGE_SIZE + 1));
      } else {
        const cursor = cursorsRef.current[pageNum - 1];
        if (!cursor) { setFetching(false); return; }
        q = query(baseQuery(), startAfter(cursor), limit(PAGE_SIZE + 1));
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      const hasNext = docs.length > PAGE_SIZE;
      const pageDocs = hasNext ? docs.slice(0, PAGE_SIZE) : docs;

      if (pageDocs.length > 0) {
        cursorsRef.current[pageNum] = pageDocs[pageDocs.length - 1];
      }

      setEntries(pageDocs.map((d) => fromDoc<AuditEntry>(d)));
      setHasMore(hasNext);
      setPage(pageNum);
    } catch (err) {
      logger.error("Failed to load audit log:", err);
      setError(formatError(err));
    } finally {
      setLoadedFor(agencyId);
      setFetching(false);
    }
  }, [enabled, agencyId, baseQuery]);

  useEffect(() => {
    cursorsRef.current = [];
    Promise.resolve().then(() => fetchPage(0));
  }, [fetchPage]);

  const loadNextPage = useCallback(() => {
    if (hasMore) fetchPage(page + 1);
  }, [hasMore, page, fetchPage]);

  const loadPrevPage = useCallback(() => {
    if (page > 0) {
      const prevPage = page - 1;
      cursorsRef.current = cursorsRef.current.slice(0, prevPage);
      fetchPage(prevPage);
    }
  }, [page, fetchPage]);

  const refresh = useCallback(() => {
    cursorsRef.current = [];
    fetchPage(0);
  }, [fetchPage]);

  const fetchAll = useCallback(async (): Promise<AuditEntry[]> => {
    if (!enabled || !agencyId) return [];
    const allEntries: AuditEntry[] = [];
    const batchSize = 500;
    let lastDoc: QueryDocumentSnapshot<unknown> | null = null;

    while (true) {
      let q;
      if (lastDoc) {
        q = query(baseQuery(), startAfter(lastDoc), limit(batchSize));
      } else {
        q = query(baseQuery(), limit(batchSize));
      }
      const snapshot = await getDocs(q);
      if (snapshot.empty) break;
      snapshot.docs.forEach((d) => allEntries.push(fromDoc<AuditEntry>(d)));
      if (snapshot.docs.length < batchSize) break;
      lastDoc = snapshot.docs[snapshot.docs.length - 1];
    }

    return allEntries;
  }, [enabled, agencyId, baseQuery]);

  const addEntry = useCallback(async (
    _user: User | null,
    action: string,
    jobId: string | null,
    jobTitle: string | null,
    details: string,
    targetUserId?: string | null,
    targetUserName?: string | null,
    changes?: Record<string, AuditChange> | null,
  ) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "createAuditEntry");
      await fn({
        action,
        jobId: jobId || null,
        jobTitle: jobTitle || null,
        targetUserId: targetUserId || null,
        targetUserName: targetUserName || null,
        details,
        changes: changes || null,
      });
    } catch (err) {
      logger.error("Failed to create audit entry:", err);
      throw new Error(formatError(err));
    }
  }, []);

  return {
    log: entries,
    loading,
    error,
    hasMore,
    hasPrev: page > 0,
    page,
    pageSize: PAGE_SIZE,
    loadNextPage,
    loadPrevPage,
    refresh,
    fetchAll,
    addEntry,
  };
}
